// src/components/pages/B2B/ProductInfo/ServiceAdvantage.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";

/* ───────────── lang helpers ───────────── */
const getCurrentLang = () =>
  getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";

const byLang = (item, lang) =>
  lang === "EN"
    ? item.EN || item.label
    : lang === "JP"
      ? item.JP || item.label
      : item.label;

/* ───────────── title (service only) ───────────── */
const SERVICE_TITLE = {
  ZH: "服務優勢",
  EN: "Service Advantages",
  JP: "サービスの強み",
};

const fonts = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed JP",
};

/* ───────────── 服務優勢 items (image + caption) ───────────── */
const SERVICE_ITEMS = [
  {
    key: "srv-01",
    label: "01   專業團隊 × 實務經驗",
    EN: "01   Professional Team × Practical Experience",
    JP: "01   専門チーム × 実務経験",
    img: "/img/products/B2B/topProductPage01.webp",
  },
  {
    key: "srv-02",
    label: "02   一站式整合客製服務",
    EN: "02   One-Stop Integrated Custom Service",
    JP: "02   ワンストップ統合カスタムサービス",
    img: "/img/products/B2B/topProductPage02.webp",
  },
  {
    key: "srv-03",
    label: "03   法規合規 × 風險控管",
    EN: "03   Regulatory Compliance × Risk Management",
    JP: "03   法令遵守 × リスク管理",
    img: "/img/products/B2B/topProductPage03.webp",
  },
  {
    key: "srv-04",
    label: "04   成效導向 × 持續優化",
    EN: "04   Performance-Driven × Continuous Optimization",
    JP: "04   成果重視 × 継続的な改善",
    img: "/img/products/B2B/topProductPage04.webp",
  },
];

const SERVICE_PRELOAD_IMAGES = [
  "/img/products/B2B/bg.webp",
  ...SERVICE_ITEMS.map((item) => item.img),
];

export default function ServiceAdvantage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [lang, setLang] = useState(getCurrentLang());
  const [serviceOpacity, setServiceOpacity] = useState(0);

  const serviceZoneRef = useRef(null);
  const serviceContentRef = useRef(null);
  const serviceTopRef = useRef(0);
  const serviceHeightRef = useRef(0);

  /* sync language with global app event */
  useEffect(() => {
    const onChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onChange);
    return () => window.removeEventListener("app:languageChanged", onChange);
  }, []);

  useEffect(() => {
    SERVICE_PRELOAD_IMAGES.forEach((src) => {
      const img = new Image();
      img.decoding = "async";
      img.src = src;
    });
  }, []);

  /* ✅ Desktop-only scroll opacity logic (mobile = no animation) */
  useEffect(() => {
    if (isMobile) {
      setServiceOpacity(1); // always visible on mobile
      return;
    }

    const updateMetrics = () => {
      const el = serviceZoneRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      serviceTopRef.current = rect.top + scrollY;
      serviceHeightRef.current = rect.height || 1;
    };

    const updateOpacity = () => {
      const zoneTop = serviceTopRef.current;
      const zoneHeight = serviceHeightRef.current || 1;
      const zoneBottom = zoneTop + zoneHeight;

      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;

      const contentEl = serviceContentRef.current;
      if (!contentEl) {
        setServiceOpacity(0);
        return;
      }

      const contentRect = contentEl.getBoundingClientRect();
      const contentTopDoc = scrollY + contentRect.top;
      const contentBottomDoc = scrollY + contentRect.bottom;
      const contentCenter = (contentTopDoc + contentBottomDoc) / 2;

      const margin = 4;

      if (
        contentBottomDoc < zoneTop + margin ||
        contentTopDoc > zoneBottom - margin
      ) {
        setServiceOpacity(0);
        return;
      }

      const fadeStart = zoneTop + zoneHeight * 0.55;
      const fadeEnd = zoneTop + zoneHeight * 0.8;

      let opacity;
      if (contentCenter <= fadeStart) opacity = 1;
      else if (contentCenter >= fadeEnd) opacity = 0;
      else {
        const t = (contentCenter - fadeStart) / (fadeEnd - fadeStart);
        opacity = 1 - t;
      }

      setServiceOpacity(Math.max(0, Math.min(1, opacity)));
    };

    const handleScroll = () => {
      updateMetrics();
      updateOpacity();
    };

    const handleResize = () => {
      updateMetrics();
      updateOpacity();
    };

    const rafId = window.requestAnimationFrame(() => {
      updateMetrics();
      updateOpacity();
    });

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleResize);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", handleResize);
    };
  }, [isMobile]);

  const serviceTitle = SERVICE_TITLE[lang] || SERVICE_TITLE.ZH;

  // ✅ MOBILE: keep desktop-like layout + vertically center in bg (no scroll animation)
  if (isMobile) {
    return (
      <Box
        component="section"
        sx={{
          bgcolor: "background.paper",
          py: { xs: 0, md: 3 },
          width: "100%",
          mx: 0,
        }}
      >
        {/* edge-to-edge background (100vw bleed like desktop) */}
        <Box
          sx={{
            position: "relative",
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
            width: "100vw",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src="/img/products/B2B/bg.webp"
            alt="服務優勢背景"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            sx={{
              width: "100%",
              height: "auto",
              display: "block",
              objectFit: "cover",
            }}
          />

          {/* overlay content: ✅ vertically centered in bg height */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center", // ✅ middle of bg image height
              justifyContent: "center",
              px: 2,
              textAlign: "center",
              color: "#ffffff",
              pointerEvents: "none",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 900 }}>
              <Typography
                sx={{
                  mb: 2,
                  fontSize: 20,
                  fontFamility: fonts[lang],
                  fontWeight: 700,
                  textShadow:
                    "0 2px 10px rgba(0,0,0,.35), 0 6px 22px rgba(0,0,0,.25)",
                }}
              >
                {serviceTitle}
              </Typography>

              {/* ✅ keep 4 side-by-side like desktop */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 1.25,
                  alignItems: "start",
                }}
              >
                {SERVICE_ITEMS.map((s) => (
                  <Box key={s.key} sx={{ textAlign: "center", minWidth: 0 }}>
                    <Box
                      component="img"
                      src={s.img}
                      alt={s.label}
                      data-eager="1"
                      loading="eager"
                      fetchPriority="high"
                      decoding="sync"
                      sx={{
                        width: "100%",
                        mx: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        objectFit: "contain",
                        // keep 4-up readable on narrow screens
                        maxHeight: 110,
                      }}
                    />
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: 10.5,
                        lineHeight: 1.25,
                        fontFamility: fonts[lang],
                        fontWeight: 400,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        textShadow:
                          "0 2px 8px rgba(0,0,0,.35), 0 6px 18px rgba(0,0,0,.2)",
                      }}
                    >
                      {byLang(s, lang)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ✅ DESKTOP: original behavior
  return (
    <Box
      component="section"
      sx={{
        bgcolor: "background.paper",
        py: { xs: 2, md: 3 },
        width: "100%",
        mx: 0,
      }}
    >
      {/* === FIXED 服務優勢 OVERLAY === */}
      <Box
        ref={serviceContentRef}
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 5,
          pointerEvents: "none",
          width: "100%",
          maxWidth: { xs: "92%", sm: "86%", md: "70%" },
          textAlign: "center",
          color: "#ffffff",
          opacity: serviceOpacity,
          transition: "opacity 400ms ease-out",
        }}
      >
        <Typography
          sx={{
            mb: { xs: 2, md: 3 },
            fontSize: { xs: 20, md: 28 },
            fontFamility: fonts[lang],
            fontWeight: 700,
          }}
        >
          {serviceTitle}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: { xs: 1, sm: 1.5, md: 3 },
            alignItems: "start",
          }}
        >
          {SERVICE_ITEMS.map((s) => (
            <Box key={s.key} sx={{ textAlign: "center", minWidth: 0 }}>
              <Box
                component="img"
                src={s.img}
                alt={s.label}
                data-eager="1"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                sx={{
                  width: "100%",
                  maxWidth: { xs: 160, sm: 200, md: 260 },
                  mx: "auto",
                  display: "block",
                  borderRadius: { xs: 1.5, md: 2 },
                  objectFit: "contain",
                }}
              />
              <Typography
                sx={{
                  mt: { xs: 0.75, md: 1.5 },
                  fontSize: { xs: 10, sm: 11.5, md: 23 },
                  lineHeight: { xs: 1.25, md: 1.4 },
                  fontFamility: fonts[lang],
                  fontWeight: 400,
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {byLang(s, lang)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* background scroll zone */}
      <Box
        ref={serviceZoneRef}
        sx={{
          mb: 4,
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          width: "100vw",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src="/img/products/B2B/bg.webp"
          alt="服務優勢背景"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          sx={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
}
